import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Modal, ModalBody, ModalHeader } from "reactstrap";
import { getCokkie } from "../../views/login/Login";
import getDataFromApi from "../getDataFromApi";
import Loader from "../Loader";
import { Theme } from "@rjsf/bootstrap-4";
import { utils, withTheme } from "@rjsf/core";
import ErrorScreen from "../error/ErrorScreen";
import { CustomWidget } from "../widgets/CustomWidgets";
import {
  CameraIcon,
  CloseIcon,
  ExclamationIcon,
  FactoryIcon,
  LabelsIcon,
  LineIcon,
  PlantIcon,
  PlatformIcon,
  VideoIcon,
} from "../../views/Common/SvgIcons/WorkflowIcons";
import ExperimentWizardIntro from "../../views/project/train/AddExperiment/ExperimentWizardIntro";
import {
  buildDatasetFlowFields,
  buildDatasetFlowWidgets,
  DatasetFlowArrayFieldTemplate,
  DatasetFlowFieldTemplate,
  DatasetFlowObjectFieldTemplate,
  replaceUnderscoresInSchemaTitles,
  sanitizeUiSchema,
} from "../../views/project/data/DatasetFormUI";
import "../../views/project/data/AddDataset.scss";
import "../../views/project/train/Experiments.scss";
import "./ReactStrapModal.scss";

// Keep the themed form component stable. Creating it inside ReactStrapModal
// remounts the complete RJSF form whenever the modal itself re-renders.
const registry = utils.getDefaultRegistry();
Theme.widgets["FileWidget"] = registry.widgets["FileWidget"];
const SchemaForm = withTheme(Theme);
const pendingFormRequests = new Map();

const getFormSchema = (url) => {
  const pendingRequest = pendingFormRequests.get(url);
  if (pendingRequest) return pendingRequest;

  const request = getDataFromApi(url, "get");
  pendingFormRequests.set(url, request);
  const clearRequest = () => {
    if (pendingFormRequests.get(url) === request) {
      pendingFormRequests.delete(url);
    }
  };
  request.then(clearRequest, clearRequest);

  return request;
};

/** Fill missing property titles from keys so Select fields still get labels. */
const ensurePropertyTitles = (schemaObj, fieldKey = "") => {
  if (!schemaObj || typeof schemaObj !== "object") return schemaObj;

  const next = { ...schemaObj };
  if (fieldKey && !String(next.title || "").trim()) {
    next.title = fieldKey.replace(/_/g, " ");
  } else if (next.title) {
    next.title = String(next.title).replace(/_/g, " ");
  }

  if (next.properties) {
    next.properties = Object.keys(next.properties).reduce((acc, key) => {
      acc[key] = ensurePropertyTitles(next.properties[key], key);
      return acc;
    }, {});
  }

  if (next.items) {
    next.items = Array.isArray(next.items)
      ? next.items.map((item) => ensurePropertyTitles(item))
      : ensurePropertyTitles(next.items);
  }

  ["oneOf", "anyOf", "allOf"].forEach((branchKey) => {
    if (Array.isArray(next[branchKey])) {
      next[branchKey] = next[branchKey].map((branch) =>
        ensurePropertyTitles(branch)
      );
    }
  });

  if (next.dependencies) {
    next.dependencies = Object.keys(next.dependencies).reduce((acc, key) => {
      const dependency = next.dependencies[key];
      if (Array.isArray(dependency?.oneOf)) {
        acc[key] = {
          ...dependency,
          oneOf: dependency.oneOf.map((dep) => ensurePropertyTitles(dep)),
        };
      } else if (dependency && typeof dependency === "object") {
        acc[key] = ensurePropertyTitles(dependency);
      } else {
        acc[key] = dependency;
      }
      return acc;
    }, {});
  }

  return next;
};

const prepareFormSchema = (schema) => {
  if (!schema) return schema;
  return ensurePropertyTitles(replaceUnderscoresInSchemaTitles(schema));
};

const FORM_ENTITY_META = {
  factory: {
    noun: "Factory",
    createVerb: "Add",
    Icon: FactoryIcon,
    subtitle: {
      add: "Configure factory details for this solution hierarchy.",
      edit: "Update factory details for this solution hierarchy.",
    },
  },
  plant: {
    noun: "Plant",
    createVerb: "Add",
    Icon: PlantIcon,
    subtitle: {
      add: "Configure plant details under the selected factory.",
      edit: "Update plant details under the selected factory.",
    },
  },
  line: {
    noun: "Production Line",
    createVerb: "Add",
    Icon: LineIcon,
    subtitle: {
      add: "Configure production line details under the selected plant.",
      edit: "Update production line details under the selected plant.",
    },
  },
  station: {
    noun: "Station",
    createVerb: "Add",
    Icon: CameraIcon,
    subtitle: {
      add: "Configure station details for this production line.",
      edit: "Update station configuration for this production line.",
    },
  },
  batch: {
    noun: "Batch",
    createVerb: "Create",
    Icon: LabelsIcon,
    subtitle: {
      add: "Create a product batch for inspection allocation.",
      edit: "Update this product batch.",
    },
  },
  defect: {
    noun: "Defect",
    createVerb: "Create",
    Icon: ExclamationIcon,
    subtitle: {
      add: "Define a defect type for this product.",
      edit: "Update this defect type.",
    },
  },
  product: {
    noun: "Product",
    createVerb: "Add",
    Icon: LabelsIcon,
    subtitle: {
      add: "Add a product to this visual inspection solution.",
      edit: "Update product details for this solution.",
    },
  },
  inspection_session: {
    noun: "Session",
    createVerb: "Create",
    Icon: VideoIcon,
    subtitle: {
      add: "Start an inspection session for this production line.",
      edit: "Update this inspection session.",
    },
  },
};

const getPathEntity = (path) => {
  const clean = String(path ?? "")
    .split("?")[0]
    .split("/")
    .filter(Boolean);
  // forms/visual-inspection/<entity>[/<id>]
  const viIndex = clean.findIndex((part) => part === "visual-inspection");
  if (viIndex >= 0 && clean[viIndex + 1]) {
    return {
      entityKey: clean[viIndex + 1],
      hasId: Boolean(clean[viIndex + 2]),
    };
  }
  return {
    entityKey: clean[2] || clean[clean.length - 1] || "form",
    hasId: clean.length > 3,
  };
};

const getFormMeta = (path) => {
  const pathStr = String(path ?? "");

  if (pathStr.includes("edit_tag")) {
    return {
      title: "Edit Tag",
      actionLabel: "Save Tag",
      stepLabel: "Tag",
      subtitle: "Update tag details.",
      Icon: LabelsIcon,
    };
  }
  if (
    pathStr.includes("forms/solutions/create") ||
    pathStr.includes("solutions/create")
  ) {
    return {
      title: "Create Solution",
      actionLabel: "Create Solution",
      stepLabel: "Solution",
      subtitle: "Define a custom solution blueprint before deploying to the edge.",
      Icon: PlatformIcon,
    };
  }
  if (
    pathStr.includes("forms/solutions/build") ||
    pathStr.includes("solutions/build")
  ) {
    return {
      title: "Edit Solution",
      actionLabel: "Save Solution",
      stepLabel: "Solution",
      subtitle: "Update this custom solution blueprint.",
      Icon: PlatformIcon,
    };
  }
  if (pathStr.includes("create_project_from_hub")) {
    return {
      title: "Create Project from Hub",
      actionLabel: "Create Project",
      stepLabel: "Project",
      subtitle: "Create a project from the selected hub item.",
      Icon: PlatformIcon,
    };
  }
  if (pathStr.includes("download_data")) {
    const parts = pathStr.split("?")[0].split("/").filter(Boolean);
    const label = parts[1]?.replace(/_/g, " ") || "Download Data";
    return {
      title: label,
      actionLabel: "Download",
      stepLabel: "Download",
      subtitle: "Choose download options.",
      Icon: PlatformIcon,
    };
  }

  const { entityKey, hasId } = getPathEntity(pathStr);
  const meta = FORM_ENTITY_META[entityKey];
  if (meta) {
    const mode = hasId ? "edit" : "add";
    const title = hasId
      ? `Edit ${meta.noun}`
      : `${meta.createVerb} ${meta.noun}`;
    return {
      title,
      actionLabel: title,
      stepLabel: meta.noun,
      subtitle: meta.subtitle[mode],
      Icon: meta.Icon,
    };
  }

  const label = String(entityKey)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const title = hasId ? `Edit ${label}` : `Add ${label}`;
  return {
    title,
    actionLabel: title,
    stepLabel: label,
    subtitle: "Complete the required information before submitting.",
    Icon: PlatformIcon,
  };
};

export function ReactStrapModal({
  open,
  close,
  path,
  onSubmit,
  images,
  project_id,
  default_form,
  reload,
  inline = false,
  transformSchemaData,
  modalClassName = "",
  variant = "modal",
}) {
  const [data, setData] = useState();
  const [alert, setAlert] = useState();
  const [formData, setFormData] = useState();
  const [submitting, setSubmitting] = useState(false);
  const closeRef = useRef(close);
  const defaultFormRef = useRef(default_form);
  const transformSchemaDataRef = useRef(transformSchemaData);
  closeRef.current = close;
  defaultFormRef.current = default_form;
  transformSchemaDataRef.current = transformSchemaData;
  const url = `${window["ENV"].API_URL}/${path}`;
  const uiSchema = {};
  const formMeta = useMemo(() => getFormMeta(path), [path]);
  const formTitle = formMeta.title;
  const formId = "hierarchy-flow-form";
  const isFlow = variant === "flow";
  const HeaderIcon = formMeta.Icon || PlatformIcon;
  const hierarchyFields = useMemo(() => buildDatasetFlowFields(), []);
  const hierarchyWidgets = useMemo(
    () => buildDatasetFlowWidgets(CustomWidget),
    []
  );

  const preparedSchema = useMemo(
    () => prepareFormSchema(data?.json_schema),
    [data?.json_schema]
  );

  const preparedUiSchema = useMemo(() => {
    const base = sanitizeUiSchema(data?.ui_schema || uiSchema || {});
    return {
      ...base,
      "ui:submitButtonOptions": {
        ...(base["ui:submitButtonOptions"] || {}),
        norender: true,
      },
    };
  }, [data?.ui_schema]);

  useEffect(() => {
    if (open) {
      setFormData(defaultFormRef.current || undefined);
    }
  }, [open, path]);

  useEffect(() => {
    let cancelled = false;
    let timeoutId;

    if (!open) {
      setData();
      setAlert();
      setFormData(defaultFormRef.current || undefined);
      setSubmitting(false);
      return;
    }

    getFormSchema(url)
      .then((r) => {
        if (cancelled) return;

        if (r === "Contact your organisation admin to add a new user") {
          setAlert({
            message: "Contact your organisation admin to add a new user",
          });
          closeRef.current(false);
        } else {
          const transform = transformSchemaDataRef.current;
          const temp = transform ? transform(r) : r;
          // Keep field labels; hide root schema title so the drawer header owns it
          if (temp?.json_schema) {
            temp.json_schema = { ...temp.json_schema, title: "" };
          }
          setData(temp);
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setAlert({ message: e.message, status: "error" });
        timeoutId = setTimeout(() => {
          setAlert(null);
          closeRef.current(false);
        }, 2000);
      });

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [open, url]);

  const handleClose = () => {
    setAlert();
    setFormData();
    setData();
    close(false);
  };

  const handleSubmit = async ({ formData: nextFormData }, e) => {
    e.preventDefault();
    setFormData(nextFormData);
    setSubmitting(true);
    try {
      await formSave(
        project_id ? url + `/${project_id}` : url,
        "post",
        nextFormData,
        setAlert,
        close,
        onSubmit,
        reload
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormChange = ({ formData: nextFormData }) => {
    setFormData(nextFormData || {});
  };

  const renderFormFields = (hideSubmit = false) => (
    <>
      {alert?.status === "error" && (
        <Alert
          color={"danger"}
          className="mb-0 shadow d-flex"
          isOpen={alert?.message}
          toggle={() => {
            setAlert();
          }}
          style={{
            maxWidth: "380px",
            borderRadius: 10,
            position: "fixed",
            top: inline || isFlow ? 70 : 30,
            left: inline || isFlow ? "50%" : undefined,
            transform: inline || isFlow ? "translateX(-50%)" : undefined,
            zIndex: inline || isFlow ? "10001" : "999",
          }}
        >
          {alert?.message}
        </Alert>
      )}
      {preparedSchema ? (
        <SchemaForm
          id={hideSubmit ? formId : undefined}
          schema={preparedSchema}
          uiSchema={
            hideSubmit
              ? preparedUiSchema
              : sanitizeUiSchema(data?.ui_schema || uiSchema || {})
          }
          formData={
            images?.length > 0
              ? { images: images, ...formData }
              : formData
          }
          onChange={handleFormChange}
          onSubmit={handleSubmit}
          widgets={hierarchyWidgets}
          fields={hierarchyFields}
          FieldTemplate={DatasetFlowFieldTemplate}
          ObjectFieldTemplate={DatasetFlowObjectFieldTemplate}
          ArrayFieldTemplate={DatasetFlowArrayFieldTemplate}
          showErrorList={false}
          noHtml5Validate
        >
          {hideSubmit ? <></> : undefined}
        </SchemaForm>
      ) : data?.detail ? (
        <ErrorScreen message={data?.detail ?? "No Form Found"} />
      ) : (
        <Loader />
      )}
    </>
  );

  if (isFlow) {
    if (!open) return null;

    return (
      <div
        className="eval-right-drawer-overlay"
        onClick={handleClose}
      >
        <div
          className="eval-right-drawer-panel p-0"
          onClick={(e) => e.stopPropagation()}
        >
          <main className="open-form evaluate-model-ui h-100 d-flex flex-column dataset-flow-shell experiment-flow-shell">
            <header className="d-flex align-items-start justify-content-between p-3 dataset-flow-shell__header">
              <div className="d-flex gap-2 align-items-start w-100">
                <HeaderIcon className="wf-icon-blue bg-primary text-white" />
                <div className="em-header flex-fill">
                  <div className="d-flex justify-content-between w-100">
                    <h2 className="main-title mb-0">{formTitle}</h2>
                    <button
                      type="button"
                      className="plain-button2 p-0 experiment-flow-shell__close"
                      onClick={handleClose}
                      aria-label="Close"
                    >
                      <CloseIcon className="wf-icon text-secondary text-secondary" />
                    </button>
                  </div>
                  <p className="sub-title mb-0">{formMeta.subtitle}</p>
                </div>
              </div>
            </header>

            <section className="p-3 dataset-wizard__card flex-fill w-100 dataset-flow-shell__body">
              {!data ? (
                <div className="dataset-flow-shell__loader">
                  <Loader />
                </div>
              ) : (
                <>
                  <ExperimentWizardIntro
                    steps={[formMeta.stepLabel]}
                    currentStepNumber={1}
                    description={formMeta.subtitle}
                  />
                  <div className="px-1 open-form experiment-form-panel">
                    {renderFormFields(true)}
                  </div>
                </>
              )}
            </section>

            {data?.json_schema && (
              <div className="dataset-footer experiment-flow-shell__footer">
                <div className="dataset-footer__actions">
                  <div />
                  <button
                    type="submit"
                    form={formId}
                    className="btn btn-primary py-2 m-0 fw-bold"
                    disabled={submitting}
                  >
                    {submitting ? "Submitting..." : formMeta.actionLabel}
                  </button>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    );
  }

  return (
    <>
      {inline ? (
        data ? (
          renderFormFields(false)
        ) : (
          <Loader />
        )
      ) : (
        data && (
          <Modal
            scrollable
            size="md"
            isOpen={open}
            className={`form-popup-width ${modalClassName}`.trim()}
            modalClassName={
              modalClassName?.includes("solution-hierarchy-form-modal")
                ? "solution-hierarchy-form-dialog"
                : undefined
            }
            centered={!modalClassName?.includes("solution-hierarchy-form-modal")}
          >
            <ModalHeader
              className="m-0 px-4"
              toggle={handleClose}
              style={{ borderBottom: "none" }}
            >
              {formTitle}
            </ModalHeader>
            <ModalBody className="px-4 pb-4 open-form">
              {renderFormFields(false)}
            </ModalBody>
          </Modal>
        )
      )}
    </>
  );
}

const getApiAlert = (result, fallback = "Something went wrong") => {
  const detail = result?.detail;

  if (typeof detail === "string") {
    return { message: detail, status: "error" };
  }

  if (detail?.message) {
    return {
      message: detail.message,
      status: detail.status || "error",
    };
  }

  if (result?.message) {
    return {
      message: result.message,
      status: result.status || "error",
    };
  }

  return { message: fallback, status: "error" };
};

export const formSave = async (
  url,
  method,
  formData,
  setAlert,
  close,
  onSubmit,
  reload
) => {
  let cokkie = getCokkie("jwtToken");

  const myHeaders = new Headers();
  myHeaders.append("Authorization", `Bearer ${cokkie}`);
  myHeaders.append("Content-Type", "application/json");

  try {
    const response = await fetch(url, {
      method,
      headers: myHeaders,
      body: JSON.stringify(formData),
    });

    const result = await response.json();

    if (!response.ok) {
      setAlert(getApiAlert(result));
      return;
    }

    if (result?.presigned_url) {
      setAlert(getApiAlert(result, "Download ready"));

      const link = document.createElement("a");
      Object.assign(link, {
        href: result.presigned_url,
        target: "_blank",
        rel: "noreferrer",
      });
      link.click();
      link.remove();
      return;
    }

    onSubmit && onSubmit(formData, result);
    close(false);

    if (reload) reload();
  } catch (error) {
    setAlert({
      message: `${error.message} or Internal Server Error`,
      status: "error",
    });
  }
};
